<template>
  <div class="quickbooks-panel">
    <hs-alert
      v-if="callbackMessage"
      :tone="callbackMessageTone"
      :title="callbackMessage.title"
      :message="callbackMessage.body"
    />

    <hs-card class="quickbooks-card" variant="flat" padding="lg">
      <div class="quickbooks-card__header">
        <div>
          <div class="quickbooks-card__eyebrow">
            <hs-icon name="book-open" :size="16" />
            Accounting Integration
          </div>
          <h3 class="quickbooks-card__title">
            QuickBooks Online
          </h3>
          <p class="quickbooks-card__subtitle">
            Connect this store to QuickBooks Online for customer, product, sale,
            supplier, and purchase Bill synchronization.
          </p>
        </div>

        <div class="quickbooks-actions">
          <hs-button
            variant="success"
            icon="link"
            :loading="connecting"
            :disabled="connecting || loading"
            @click="startConnectFlow"
          >
            {{ connecting ? "Opening QuickBooks..." : connectButtonLabel }}
          </hs-button>
          <hs-button
            v-if="isConnected"
            variant="secondary"
            icon="unlink"
            :loading="disconnecting"
            :disabled="disconnecting || loading"
            @click="disconnect"
          >
            {{ disconnecting ? "Disconnecting..." : "Disconnect" }}
          </hs-button>
          <hs-button
            v-if="canRunSync"
            variant="secondary"
            icon="refresh-cw"
            :disabled="isSyncBusy || loading"
            @click="triggerSync"
          >
            {{ syncButtonLabel }}
          </hs-button>
        </div>
      </div>

      <div v-if="loading" class="quickbooks-loading">
        <hs-skeleton variant="card" height="8.5rem" />
        <div class="quickbooks-loading__grid">
          <hs-skeleton
            v-for="index in 4"
            :key="`quickbooks-summary-skeleton-${index}`"
            variant="card"
            height="6.75rem"
          />
        </div>
      </div>

      <template v-else>
        <div class="quickbooks-summary-grid">
          <article class="quickbooks-summary-card">
            <div class="quickbooks-summary-card__top">
              <span>Connection Status</span>
              <hs-badge :tone="isConnected ? 'success' : 'neutral'" size="sm">
                {{ isConnected ? "Active" : "Idle" }}
              </hs-badge>
            </div>
            <strong>
              {{ isConnected ? "Connected" : "Not connected" }}
            </strong>
            <p>
              {{ connectedCompanyLabel }}
            </p>
          </article>

          <article class="quickbooks-summary-card">
            <div class="quickbooks-summary-card__top">
              <span>Environment</span>
              <hs-badge :tone="integrationEnabled ? 'success' : 'warning'" size="sm">
                {{ integrationEnabled ? "Enabled" : "Off" }}
              </hs-badge>
            </div>
            <strong>
              {{ environmentLabel }}
            </strong>
            <p>
              {{ integrationEnabled ? "Integration enabled" : "Integration toggle off" }}
            </p>
          </article>

          <article class="quickbooks-summary-card">
            <div class="quickbooks-summary-card__top">
              <span>Last Sync</span>
              <hs-badge :tone="lastSyncTone" size="sm">
                {{ syncStatus?.last_sync_status || "No run" }}
              </hs-badge>
            </div>
            <strong>
              {{ lastSyncLabel }}
            </strong>
            <p>
              {{ syncStatus?.last_sync_status || "No sync recorded yet" }}
            </p>
          </article>

          <article class="quickbooks-summary-card">
            <div class="quickbooks-summary-card__top">
              <span>Auto Sync</span>
              <hs-badge :tone="settingsForm.auto_sync_enabled ? 'success' : 'neutral'" size="sm">
                {{ settingsForm.auto_sync_enabled ? "On" : "Off" }}
              </hs-badge>
            </div>
            <strong>
              {{ settingsForm.auto_sync_enabled ? "Enabled" : "Disabled" }}
            </strong>
            <p>
              Items: push {{ toggleLabel(settingsForm.push_items) }} / pull {{ toggleLabel(settingsForm.pull_items) }} • Sales: push {{ toggleLabel(settingsForm.push_sales) }}
            </p>
          </article>
        </div>

        <hs-alert
          v-if="showEnableWarning"
          tone="warning"
          title="Integration toggle is off"
          message="QuickBooks is connected, but this store-level integration toggle is still off. Enable the integration below and save before manual sync will work."
        />

        <div
          v-if="syncFeedback.visible"
          class="quickbooks-sync-feedback"
          :class="`quickbooks-sync-feedback--${syncProgress.state || 'waiting'}`"
        >
          <div class="quickbooks-sync-feedback__content">
            <div>
              <div class="quickbooks-sync-feedback__heading">
                <p>
                  {{ syncFeedback.title }}
                </p>
                <hs-badge :tone="syncFeedbackTone" size="sm">
                  {{ syncFeedback.stateLabel }}
                </hs-badge>
              </div>
              <p class="quickbooks-sync-feedback__message">
                {{ syncFeedback.message }}
              </p>
              <p
                v-if="syncFeedback.meta"
                class="quickbooks-sync-feedback__meta"
              >
                {{ syncFeedback.meta }}
              </p>
            </div>

            <hs-button
              v-if="syncFeedback.showRefresh"
              variant="ghost"
              size="sm"
              icon="refresh-cw"
              :disabled="loading"
              @click="refreshSyncProgress"
            >
              Refresh status
            </hs-button>
          </div>

          <div
            v-if="syncFeedback.showPulse"
            class="quickbooks-sync-feedback__progress"
          >
            <div></div>
          </div>
        </div>

        <div class="quickbooks-main-grid">
          <section class="quickbooks-section">
            <div class="quickbooks-section__header">
              <div>
                <h4>
                  Store Sync Controls
                </h4>
                <p>
                  Connection stores the QuickBooks token. These controls decide whether
                  this store can sync and which entities participate.
                </p>
              </div>

              <div class="quickbooks-actions">
                <hs-button
                  v-if="hasSettingChanges"
                  variant="secondary"
                  size="sm"
                  :disabled="savingSettings"
                  @click="resetSettingsForm"
                >
                  Reset
                </hs-button>
                <hs-button
                  size="sm"
                  icon="save"
                  :loading="savingSettings"
                  :disabled="!hasSettingChanges || savingSettings"
                  @click="saveSettings"
                >
                  {{ savingSettings ? "Saving..." : "Save Settings" }}
                </hs-button>
              </div>
            </div>

            <div class="quickbooks-toggle-grid">
              <article
                v-for="toggle in settingsToggles"
                :key="toggle.field"
                class="quickbooks-toggle-card"
              >
                <hs-toggle
                  v-model="settingsForm[toggle.field]"
                  :label="toggle.label"
                  :description="toggle.description"
                  :disabled="savingSettings"
                />
              </article>
            </div>
          </section>

          <section v-if="isConnected" class="quickbooks-section quickbooks-section--wide">
            <div class="quickbooks-section__header">
              <div>
                <h4>
                  Payment Account Mapping
                </h4>
                <p>
                  Map your local payment accounts to QuickBooks deposit accounts and payment methods.
                  This controls where sales revenue is deposited in QuickBooks.
                </p>
              </div>
              <hs-button
                v-if="!loadingMappings"
                variant="secondary"
                size="sm"
                icon="refresh-cw"
                @click="loadPaymentMappingData"
              >
                Refresh
              </hs-button>
            </div>

            <div v-if="loadingMappings" class="quickbooks-loading-list">
              <hs-skeleton variant="card" height="7rem" />
              <hs-skeleton variant="card" height="7rem" />
            </div>

            <div v-else-if="localPaymentAccounts.length === 0" class="quickbooks-empty-note">
              No payment accounts found for this store. Create payment accounts first, then map them here.
            </div>

            <div v-else class="quickbooks-list">
              <article
                v-for="account in localPaymentAccounts"
                :key="account.id"
                class="quickbooks-record-card"
              >
                <div class="quickbooks-record-card__header">
                  <div class="min-w-0">
                    <p class="quickbooks-record-card__title">
                      {{ account.name }}
                      <hs-badge v-if="account.default" tone="primary" size="sm">
                        Default
                      </hs-badge>
                    </p>
                    <p class="quickbooks-record-card__meta">
                      Local Payment Account ID: {{ account.id }}
                    </p>
                  </div>
                </div>

                <div class="quickbooks-form-grid">
                  <div>
                    <label class="quickbooks-field-label">
                      QBO Deposit Account
                    </label>
                    <hs-select
                      :model-value="getMappingField(account.id, 'qbo_deposit_account_id')"
                      :options="depositAccountOptions"
                      placeholder="Not mapped"
                      :disabled="savingMapping === account.id"
                      @update:modelValue="updateMapping(account.id, 'deposit', $event)"
                    />
                  </div>

                  <div>
                    <label class="quickbooks-field-label">
                      QBO Payment Method
                    </label>
                    <hs-select
                      :model-value="getMappingField(account.id, 'qbo_payment_method_id')"
                      :options="paymentMethodOptions"
                      placeholder="Not mapped"
                      :disabled="savingMapping === account.id"
                      @update:modelValue="updateMapping(account.id, 'payment_method', $event)"
                    />
                  </div>
                </div>

                <p
                  v-if="getMappingField(account.id, 'qbo_deposit_account_name')"
                  class="quickbooks-record-card__meta quickbooks-record-card__meta--strong"
                >
                  Mapped to: {{ getMappingField(account.id, 'qbo_deposit_account_name') }}
                  <span v-if="getMappingField(account.id, 'qbo_payment_method_name')">
                    via {{ getMappingField(account.id, 'qbo_payment_method_name') }}
                  </span>
                </p>
              </article>
            </div>
          </section>

          <section class="quickbooks-section">
            <div class="quickbooks-section__header">
              <div>
                <h4>
                  Sync Activity
                </h4>
                <p>
                  Stats come from the sync metadata already tracked by the backend,
                  including the latest failed push per entity type.
                </p>
              </div>
              <div class="quickbooks-stat-pills">
                <span>
                  Total {{ aggregateStats.total }}
                </span>
                <span class="quickbooks-stat-pill--success">
                  Synced {{ aggregateStats.synced }}
                </span>
                <span class="quickbooks-stat-pill--warning">
                  Pending {{ aggregateStats.pending }}
                </span>
                <span class="quickbooks-stat-pill--danger">
                  Failed {{ aggregateStats.failed }}
                </span>
              </div>
            </div>

            <div v-if="hasTrackedSyncData" class="quickbooks-stats-grid">
              <article
                v-for="entry in statsEntries"
                :key="entry.key"
                class="quickbooks-stat-card"
              >
                <div class="quickbooks-stat-card__header">
                  <div>
                    <p class="quickbooks-stat-card__title">
                      {{ entry.label }}
                    </p>
                    <p class="quickbooks-stat-card__meta">
                      {{ entry.stats.total }} tracked records
                    </p>
                  </div>
                  <hs-badge tone="neutral" size="sm">
                    {{ syncRatioLabel(entry.stats) }}
                  </hs-badge>
                </div>

                <div class="quickbooks-progress">
                  <div>
                    <span
                      class="bg-emerald-500"
                      :style="segmentStyle(entry.stats.synced, entry.stats.total)"
                    ></span>
                    <span
                      class="bg-amber-400"
                      :style="segmentStyle(entry.stats.pending, entry.stats.total)"
                    ></span>
                    <span
                      class="bg-red-500"
                      :style="segmentStyle(entry.stats.failed, entry.stats.total)"
                    ></span>
                  </div>
                </div>

                <div class="quickbooks-stat-breakdown">
                  <div>
                    <p class="quickbooks-text-success">
                      {{ entry.stats.synced }}
                    </p>
                    <p>Synced</p>
                  </div>
                  <div>
                    <p class="quickbooks-text-warning">
                      {{ entry.stats.pending }}
                    </p>
                    <p>Pending</p>
                  </div>
                  <div>
                    <p class="quickbooks-text-danger">
                      {{ entry.stats.failed }}
                    </p>
                    <p>Failed</p>
                  </div>
                </div>

                <div
                  v-if="entry.stats.recent_error?.message"
                  class="quickbooks-inline-error"
                  :title="entry.stats.recent_error.message"
                >
                  <p>Latest error</p>
                  <span>
                    {{ summarizeSyncError(entry.stats.recent_error.message) }}
                  </span>
                  <small
                    v-if="recentErrorMetaLabel(entry.key, entry.stats.recent_error)"
                  >
                    {{ recentErrorMetaLabel(entry.key, entry.stats.recent_error) }}
                  </small>
                </div>
              </article>
            </div>

            <p
              v-else
              class="quickbooks-empty-note"
            >
              No QuickBooks metadata has been recorded yet for this store. After sync runs,
              these counters will reflect tracked products, customers, sales, suppliers,
              and purchase Bills.
            </p>
          </section>
        </div>

        <div class="quickbooks-secondary-grid">
          <section class="quickbooks-section">
            <div class="quickbooks-section__header">
              <div>
                <h4>
                  Recent Sync Records
                </h4>
                <p>
                  Per-record outbound pushes and inbound pull observations captured by the backend.
                </p>
              </div>
              <hs-badge tone="neutral" size="sm">
                {{ recentEvents.length }} shown
              </hs-badge>
            </div>

            <div v-if="recentEvents.length" class="quickbooks-list">
              <article
                v-for="event in recentEvents"
                :key="`event-${event.id}`"
                class="quickbooks-record-card"
              >
                <div class="quickbooks-record-card__header">
                  <div class="min-w-0">
                    <div class="quickbooks-chip-row">
                      <hs-badge tone="neutral" size="sm">
                        {{ formatEventEntity(event) }}
                      </hs-badge>
                      <hs-badge :tone="directionTone(event.direction)" size="sm">
                        {{ formatDirection(event.direction) }}
                      </hs-badge>
                      <hs-badge :tone="statusTone(event.status)" size="sm">
                        {{ formatStatus(event.status) }}
                      </hs-badge>
                    </div>
                    <p class="quickbooks-record-card__body">
                      {{ event.message || "No additional message recorded." }}
                    </p>
                    <p
                      v-if="eventMetaLabel(event)"
                      class="quickbooks-record-card__meta"
                    >
                      {{ eventMetaLabel(event) }}
                    </p>
                  </div>

                  <p class="quickbooks-record-card__date">
                    {{ formatDate(event.occurred_at || event.created_at) }}
                  </p>
                </div>
              </article>
            </div>

            <p
              v-else
              class="quickbooks-empty-note"
            >
              No granular sync records have been captured yet for this store.
            </p>

            <p class="quickbooks-footnote">
              Inbound records currently mean the backend pulled QBO Items, Customers, or Vendors during reconciliation. Local apply for inbound records is not implemented yet.
            </p>
          </section>

          <section class="quickbooks-section">
            <div class="quickbooks-section__header">
              <div>
                <h4>
                  Recent Sync Runs
                </h4>
                <p>
                  Manual syncs, automatic outbound hooks, retries, and reconciliations now create run entries.
                </p>
              </div>
              <hs-badge tone="neutral" size="sm">
                {{ recentRuns.length }} shown
              </hs-badge>
            </div>

            <div v-if="recentRuns.length" class="quickbooks-list">
              <article
                v-for="run in recentRuns"
                :key="`run-${run.id}`"
                class="quickbooks-record-card"
              >
                <div class="quickbooks-record-card__header">
                  <div class="min-w-0">
                    <div class="quickbooks-chip-row">
                      <hs-badge tone="neutral" size="sm">
                        {{ formatTriggerSource(run.trigger_source) }}
                      </hs-badge>
                      <hs-badge :tone="directionTone(run.direction)" size="sm">
                        {{ formatDirection(run.direction) }}
                      </hs-badge>
                      <hs-badge :tone="statusTone(run.status)" size="sm">
                        {{ formatStatus(run.status) }}
                      </hs-badge>
                    </div>
                    <p class="quickbooks-record-card__title quickbooks-record-card__title--spaced">
                      {{ formatRunScope(run.entity_scope) }}
                    </p>
                    <p class="quickbooks-record-card__meta">
                      {{ summarizeRun(run) }}
                    </p>
                  </div>

                  <p class="quickbooks-record-card__date">
                    {{ formatDate(run.started_at || run.created_at) }}
                  </p>
                </div>
              </article>
            </div>

            <p
              v-else
              class="quickbooks-empty-note"
            >
              No sync runs have been recorded yet for this store.
            </p>
          </section>
        </div>

        <hs-alert
          v-if="errorMessage"
          tone="danger"
          title="QuickBooks settings error"
          :message="errorMessage"
        />
      </template>
    </hs-card>
  </div>
</template>

<script>
import { toast } from "vue3-toastify";
import api from "@/helper/api";
import http from "@/helper/http";
import {
  HsAlert,
  HsBadge,
  HsButton,
  HsCard,
  HsIcon,
  HsSelect,
  HsSkeleton,
  HsToggle,
} from "@/components/ui";

const SETTINGS_FIELDS = [
  "qbo_enabled",
  "auto_sync_enabled",
  "push_items",
  "pull_items",
  "push_customers",
  "pull_customers",
  "push_sales",
  "push_suppliers",
  "pull_suppliers",
  "push_purchases",
];

const ENTITY_LABELS = {
  products: "Products",
  services: "Services",
  customers: "Customers",
  sales: "Sales",
  suppliers: "Suppliers",
  purchases: "Purchase Bills",
};

const ENTITY_RECORD_LABELS = {
  products: "Product",
  services: "Service",
  customers: "Customer",
  sales: "Sale cart",
  suppliers: "Supplier",
  purchases: "Purchase batch",
};

const EVENT_ENTITY_LABELS = {
  PRODUCT: "Product",
  SERVICE: "Service",
  CUSTOMER: "Customer",
  SALE: "Sale",
  SUPPLIER: "Supplier",
  PURCHASE: "Purchase Bill",
  RECONCILIATION: "Reconciliation",
};

const RUN_SCOPE_LABELS = {
  ALL: "All enabled entities",
  PRODUCT: "Products",
  PRODUCTS: "Products",
  SERVICE: "Services",
  SERVICES: "Services",
  CUSTOMER: "Customers",
  CUSTOMERS: "Customers",
  SUPPLIER: "Suppliers",
  SUPPLIERS: "Suppliers",
  SALE: "Sales",
  SALES: "Sales",
  PURCHASE: "Purchase Bills",
  PURCHASES: "Purchase Bills",
  ITEM: "Items",
  VENDOR: "Vendors",
  FAILED_METADATA: "Failed outbound metadata",
};

const createDefaultSettingsForm = () => ({
  qbo_enabled: false,
  auto_sync_enabled: false,
  push_items: true,
  pull_items: true,
  push_customers: true,
  pull_customers: true,
  push_sales: true,
  push_suppliers: true,
  pull_suppliers: true,
  push_purchases: true,
});

const createDefaultSyncDetails = () => ({
  recent_runs: [],
  recent_events: [],
});

const createDefaultSyncProgress = () => ({
  active: false,
  state: "idle",
  title: "",
  message: "",
  meta: "",
  requestedAt: null,
  baselineRunId: 0,
  trackedRunId: null,
  timeoutAt: null,
});

const MANUAL_SYNC_TRIGGER_SOURCE = "MANUAL_SYNC";
const SYNC_POLL_INTERVAL_MS = 3000;
const SYNC_POLL_TIMEOUT_MS = 90000;

export default {
  name: "QuickbooksIntegrationCard",
  components: {
    HsAlert,
    HsBadge,
    HsButton,
    HsCard,
    HsIcon,
    HsSelect,
    HsSkeleton,
    HsToggle,
  },
  props: {
    storeId: {
      type: [String, Number],
      default: null,
    },
    callbackQuery: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      connecting: false,
      disconnecting: false,
      syncing: false,
      savingSettings: false,
      settings: null,
      syncStatus: null,
      syncDetails: createDefaultSyncDetails(),
      syncProgress: createDefaultSyncProgress(),
      syncPollTimer: null,
      settingsForm: createDefaultSettingsForm(),
      initialSettingsForm: createDefaultSettingsForm(),
      errorMessage: "",
      callbackToastKey: "",
      // Payment account mapping
      paymentAccountMappings: [],
      localPaymentAccounts: [],
      qboDepositAccounts: [],
      qboPaymentMethods: [],
      loadingMappings: false,
      savingMapping: null,
    };
  },
  computed: {
    isConnected() {
      return Boolean(this.syncStatus?.connected || this.settings?.is_connected);
    },
    integrationEnabled() {
      return Boolean(this.settingsForm.qbo_enabled);
    },
    canRunSync() {
      return this.isConnected && this.integrationEnabled;
    },
    isSyncBusy() {
      return (
        this.syncing ||
        (this.syncProgress.active && ["waiting", "running"].includes(this.syncProgress.state))
      );
    },
    syncButtonLabel() {
      if (this.syncing) {
        return "Queuing sync...";
      }

      if (this.syncProgress.active && this.syncProgress.state === "waiting") {
        return "Waiting for worker...";
      }

      if (this.syncProgress.active && this.syncProgress.state === "running") {
        return "Sync in progress...";
      }

      return "Run Sync";
    },
    showEnableWarning() {
      return this.isConnected && !this.integrationEnabled;
    },
    hasSettingChanges() {
      return SETTINGS_FIELDS.some(
        (field) => this.settingsForm[field] !== this.initialSettingsForm[field],
      );
    },
    connectButtonLabel() {
      return this.isConnected ? "Reconnect QuickBooks" : "Connect QuickBooks";
    },
    settingsToggles() {
      return [
        {
          field: "qbo_enabled",
          label: "Enable QuickBooks for this store",
          description:
            "Required before manual sync or any background QuickBooks hooks can run.",
        },
        {
          field: "auto_sync_enabled",
          label: "Enable automatic sync hooks",
          description:
            "Lets background entity tasks push local changes when QBO sync hooks fire.",
        },
        {
          field: "push_items",
          label: "Push items to QuickBooks",
          description:
            "Push products and services to QuickBooks when created or updated locally.",
        },
        {
          field: "pull_items",
          label: "Pull items from QuickBooks",
          description:
            "Pull products and services from QuickBooks during reconciliation.",
        },
        {
          field: "push_customers",
          label: "Push customers to QuickBooks",
          description:
            "Push customers to QuickBooks when created or updated locally.",
        },
        {
          field: "pull_customers",
          label: "Pull customers from QuickBooks",
          description:
            "Pull customers from QuickBooks during reconciliation.",
        },
        {
          field: "push_sales",
          label: "Push sales to QuickBooks",
          description: "Push completed sales as SalesReceipts to QuickBooks.",
        },
        {
          field: "push_suppliers",
          label: "Push suppliers to QuickBooks",
          description: "Push suppliers as Vendors to QuickBooks.",
        },
        {
          field: "pull_suppliers",
          label: "Pull suppliers from QuickBooks",
          description: "Pull Vendors from QuickBooks during reconciliation.",
        },
        {
          field: "push_purchases",
          label: "Push purchases to QuickBooks",
          description: "Push purchase batches as Bills to QuickBooks.",
        },
      ];
    },
    depositAccountOptions() {
      return [
        { value: "", label: "Not mapped" },
        ...this.qboDepositAccounts.map((account) => ({
          value: account.id,
          label: `${account.name} (${account.account_type})`,
        })),
      ];
    },
    paymentMethodOptions() {
      return [
        { value: "", label: "Not mapped" },
        ...this.qboPaymentMethods.map((method) => ({
          value: method.id,
          label: method.name,
        })),
      ];
    },
    environmentLabel() {
      if (!this.settings) {
        return "Not configured";
      }
      return this.settings?.is_sandbox ? "Sandbox" : "Production";
    },
    connectedCompanyLabel() {
      if (!this.isConnected) {
        return "OAuth connection not completed yet.";
      }
      return this.settings?.company_name || "Connected company available";
    },
    lastSyncLabel() {
      const value = this.syncStatus?.last_sync_at || this.settings?.last_sync_at;
      if (!value) {
        return "Never";
      }
      return this.formatDate(value);
    },
    statsEntries() {
      return Object.keys(ENTITY_LABELS).map((key) => ({
        key,
        label: ENTITY_LABELS[key],
        stats: this.normalizeStats(this.syncStatus?.stats?.[key]),
      }));
    },
    aggregateStats() {
      return this.statsEntries.reduce(
        (totals, entry) => ({
          total: totals.total + entry.stats.total,
          synced: totals.synced + entry.stats.synced,
          pending: totals.pending + entry.stats.pending,
          failed: totals.failed + entry.stats.failed,
        }),
        { total: 0, synced: 0, pending: 0, failed: 0 },
      );
    },
    hasTrackedSyncData() {
      return this.statsEntries.some((entry) => entry.stats.total > 0);
    },
    recentEvents() {
      return Array.isArray(this.syncDetails?.recent_events)
        ? this.syncDetails.recent_events
        : [];
    },
    recentRuns() {
      return Array.isArray(this.syncDetails?.recent_runs)
        ? this.syncDetails.recent_runs
        : [];
    },
    latestManualSyncRun() {
      const baselineRunId = Number(this.syncProgress?.baselineRunId || 0);
      const requestedAtValue = this.syncProgress?.requestedAt;
      const requestedAt = requestedAtValue
        ? new Date(requestedAtValue).getTime()
        : null;

      return this.recentRuns.find((run) => {
        if (run?.trigger_source !== MANUAL_SYNC_TRIGGER_SOURCE) {
          return false;
        }

        const runId = Number(run?.id || 0);
        if (runId > baselineRunId) {
          return true;
        }

        if (!requestedAt) {
          return false;
        }

        const startedAt = run?.started_at || run?.created_at;
        if (!startedAt) {
          return false;
        }

        return new Date(startedAt).getTime() >= requestedAt - 5000;
      });
    },
    syncFeedback() {
      if (!this.syncProgress.active) {
        return {
          visible: false,
        };
      }

      const run = this.latestManualSyncRun;
      const state = this.syncProgress.state;
      const message = run
        ? this.summarizeRun(run)
        : this.syncProgress.message;
      const meta = this.syncProgress.meta ||
        (run
          ? [
              this.formatRunScope(run.entity_scope),
              run.started_at || run.created_at
                ? this.formatDate(run.started_at || run.created_at)
                : null,
            ]
              .filter(Boolean)
              .join(" • ")
          : "");

      return {
        visible: true,
        title: this.syncProgress.title,
        stateLabel: this.formatSyncProgressState(state),
        message: this.syncProgress.message || message,
        meta,
        showPulse: state === "waiting" || state === "running",
        showRefresh: state === "timeout" || state === "failed",
      };
    },
    syncFeedbackClasses() {
      return this.syncProgressClassMap(this.syncProgress.state).container;
    },
    syncFeedbackChipClasses() {
      return this.syncProgressClassMap(this.syncProgress.state).chip;
    },
    syncFeedbackTone() {
      const tones = {
        waiting: "info",
        running: "info",
        success: "success",
        failed: "danger",
        timeout: "warning",
      };
      return tones[this.syncProgress.state] || "info";
    },
    callbackMessage() {
      const success = String(this.callbackQuery?.success || "").toLowerCase();
      const error = this.callbackQuery?.error;
      const companyName = this.callbackQuery?.company_name;

      if (success === "true") {
        return {
          title: "QuickBooks connected",
          body: companyName
            ? `The store was connected successfully to ${companyName}.`
            : "The store was connected successfully to QuickBooks Online.",
        };
      }

      if (success === "false" || error) {
        return {
          title: "QuickBooks connection failed",
          body: error || "The QuickBooks callback returned an unknown error.",
        };
      }

      return null;
    },
    callbackMessageClass() {
      const success = String(this.callbackQuery?.success || "").toLowerCase();
      return success === "true"
        ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200"
        : "border-red-200 bg-red-50 text-red-800 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200";
    },
    callbackMessageTone() {
      const success = String(this.callbackQuery?.success || "").toLowerCase();
      return success === "true" ? "success" : "danger";
    },
    lastSyncTone() {
      const normalized = String(this.syncStatus?.last_sync_status || "").toUpperCase();
      if (!normalized) {
        return "neutral";
      }
      if (normalized.includes("FAIL")) {
        return "danger";
      }
      if (normalized.includes("PENDING") || normalized.includes("RUNNING")) {
        return "warning";
      }
      return "success";
    },
  },
  watch: {
    storeId: {
      handler() {
        this.stopSyncPolling();
        this.syncProgress = createDefaultSyncProgress();
        this.loadQuickbooksState();
      },
      immediate: true,
    },
    callbackMessage: {
      handler(message) {
        if (!message) {
          return;
        }

        const signature = JSON.stringify(this.callbackQuery || {});
        if (signature === this.callbackToastKey) {
          return;
        }

        this.callbackToastKey = signature;
        const success = String(this.callbackQuery?.success || "").toLowerCase();
        if (success === "true") {
          toast.success(message.body);
        } else {
          toast.error(message.body);
        }
      },
      immediate: true,
    },
    latestManualSyncRun: {
      handler(run) {
        this.updateSyncProgressFromRun(run);
      },
    },
  },
  beforeUnmount() {
    this.stopSyncPolling();
  },
  methods: {
    normalizeStats(stats) {
      return {
        total: Number(stats?.total || 0),
        synced: Number(stats?.synced || 0),
        pending: Number(stats?.pending || 0),
        failed: Number(stats?.failed || 0),
        recent_error: stats?.recent_error || null,
      };
    },
    normalizeSyncDetails(details) {
      return {
        recent_runs: Array.isArray(details?.recent_runs)
          ? details.recent_runs
          : [],
        recent_events: Array.isArray(details?.recent_events)
          ? details.recent_events
          : [],
      };
    },
    formatSyncProgressState(state) {
      const labels = {
        waiting: "Queued",
        running: "Running",
        success: "Completed",
        failed: "Failed",
        timeout: "No worker activity",
      };

      return labels[state] || "Sync";
    },
    syncProgressClassMap(state) {
      const defaults = {
        container:
          "border-sky-200 bg-sky-50 text-sky-900 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-100",
        chip:
          "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200",
      };

      const classes = {
        waiting: defaults,
        running: defaults,
        success: {
          container:
            "border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-100",
          chip:
            "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200",
        },
        failed: {
          container:
            "border-red-200 bg-red-50 text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100",
          chip:
            "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200",
        },
        timeout: {
          container:
            "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100",
          chip:
            "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200",
        },
      };

      return classes[state] || defaults;
    },
    summarizeSyncError(message) {
      if (!message) {
        return "";
      }

      let summary = String(message);

      try {
        const parsed = JSON.parse(summary);
        const firstError = parsed?.Fault?.Error?.[0];
        summary =
          firstError?.Detail ||
          firstError?.Message ||
          parsed?.error ||
          summary;
      } catch (error) {
        // Use the raw error string when it is not JSON.
      }

      return summary.length > 220 ? `${summary.slice(0, 217)}...` : summary;
    },
    formatDirection(direction) {
      if (!direction) {
        return "Unknown";
      }

      return String(direction).toLowerCase() === "inbound"
        ? "Inbound"
        : "Outbound";
    },
    formatStatus(status) {
      if (!status) {
        return "Unknown";
      }

      return String(status).replace(/_/g, " ");
    },
    formatTriggerSource(triggerSource) {
      if (!triggerSource) {
        return "Unknown trigger";
      }

      return String(triggerSource)
        .toLowerCase()
        .split("_")
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
    },
    formatEventEntity(event) {
      const label = EVENT_ENTITY_LABELS[event?.entity_type];
      return label || event?.entity_type || "Sync event";
    },
    formatRunScope(entityScope) {
      if (!entityScope) {
        return "Sync run";
      }

      return RUN_SCOPE_LABELS[entityScope] || entityScope;
    },
    directionChipClass(direction) {
      return String(direction).toLowerCase() === "inbound"
        ? "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200"
        : "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200";
    },
    statusChipClass(status) {
      const normalized = String(status || "").toUpperCase();
      if (normalized === "SUCCESS") {
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-200";
      }
      if (normalized === "FAILED") {
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-200";
      }
      if (normalized === "SKIPPED") {
        return "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200";
      }
      if (normalized === "PULLED") {
        return "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-200";
      }
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-200";
    },
    directionTone(direction) {
      return String(direction).toLowerCase() === "inbound" ? "info" : "primary";
    },
    statusTone(status) {
      const normalized = String(status || "").toUpperCase();
      if (normalized === "SUCCESS" || normalized === "PULLED") {
        return "success";
      }
      if (normalized === "FAILED") {
        return "danger";
      }
      if (normalized === "SKIPPED") {
        return "neutral";
      }
      return "warning";
    },
    eventMetaLabel(event) {
      const parts = [];

      if (event?.local_record_id) {
        parts.push(`Local #${event.local_record_id}`);
      }

      if (event?.qbo_entity_type || event?.qbo_entity_id) {
        parts.push(
          [event.qbo_entity_type, event.qbo_entity_id ? `#${event.qbo_entity_id}` : null]
            .filter(Boolean)
            .join(" "),
        );
      }

      if (event?.trigger_source) {
        parts.push(this.formatTriggerSource(event.trigger_source));
      }

      return parts.join(" • ");
    },
    summarizeRun(run) {
      if (run?.last_error) {
        return this.summarizeSyncError(run.last_error);
      }

      const summary = run?.summary;
      if (!summary || typeof summary !== "object") {
        return "No additional summary recorded.";
      }

      const parts = [];
      if (typeof summary.products === "number") {
        parts.push(`${summary.products} product retries queued`);
      } else if (summary.products?.pulled !== undefined) {
        parts.push(`${summary.products.pulled} products pulled`);
      }

      if (typeof summary.customers === "number") {
        parts.push(`${summary.customers} customer retries queued`);
      } else if (summary.customers?.pulled !== undefined) {
        parts.push(`${summary.customers.pulled} customers pulled`);
      }

      if (typeof summary.suppliers === "number") {
        parts.push(`${summary.suppliers} supplier retries queued`);
      } else if (summary.vendors?.pulled !== undefined) {
        parts.push(`${summary.vendors.pulled} vendors pulled`);
      }

      if (typeof summary.purchases === "number") {
        parts.push(`${summary.purchases} purchase retries queued`);
      }

      if (!parts.length && summary.reason) {
        parts.push(summary.reason);
      }

      return parts.length ? parts.join(" • ") : "No additional summary recorded.";
    },
    recentErrorMetaLabel(entityKey, recentError) {
      if (!recentError) {
        return "";
      }

      const parts = [];
      const recordLabel = ENTITY_RECORD_LABELS[entityKey] || "Record";

      if (recentError.local_record_id) {
        parts.push(`${recordLabel} #${recentError.local_record_id}`);
      }

      if (recentError.qbo_entity_type) {
        parts.push(recentError.qbo_entity_type);
      }

      if (recentError.at) {
        parts.push(this.formatDate(recentError.at));
      }

      return parts.join(" • ");
    },
    syncRatioLabel(stats) {
      if (!stats.total) {
        return "0 / 0";
      }
      return `${stats.synced} / ${stats.total}`;
    },
    segmentStyle(value, total) {
      if (!total || !value) {
        return { width: "0%" };
      }

      return {
        width: `${(value / total) * 100}%`,
      };
    },
    syncSettingsForm(settings = this.settings) {
      const nextForm = {
        ...createDefaultSettingsForm(),
      };

      if (settings && typeof settings === "object") {
        SETTINGS_FIELDS.forEach((field) => {
          if (typeof settings[field] === "boolean") {
            nextForm[field] = settings[field];
          }
        });
      }

      this.settingsForm = { ...nextForm };
      this.initialSettingsForm = { ...nextForm };
    },
    resetSettingsForm() {
      this.settingsForm = { ...this.initialSettingsForm };
      this.errorMessage = "";
    },
    stopSyncPolling() {
      if (this.syncPollTimer) {
        clearTimeout(this.syncPollTimer);
        this.syncPollTimer = null;
      }
    },
    scheduleSyncPoll() {
      this.stopSyncPolling();
      this.syncPollTimer = window.setTimeout(() => {
        this.pollSyncProgress();
      }, SYNC_POLL_INTERVAL_MS);
    },
    beginSyncTracking() {
      const latestRunId = Number(this.recentRuns?.[0]?.id || 0);
      const requestedAt = new Date().toISOString();

      this.syncProgress = {
        active: true,
        state: "waiting",
        title: "QuickBooks sync queued",
        message:
          "The request was accepted. Waiting for a worker to start the reconciliation run and report progress.",
        meta: "This panel will refresh automatically until the run finishes or times out.",
        requestedAt,
        baselineRunId: latestRunId,
        trackedRunId: null,
        timeoutAt: Date.now() + SYNC_POLL_TIMEOUT_MS,
      };
    },
    finishSyncTracking({ state, title, message, meta, toastMessage, toastType }) {
      this.stopSyncPolling();
      this.syncProgress = {
        ...this.syncProgress,
        active: true,
        state,
        title,
        message,
        meta: meta || this.syncProgress.meta,
      };

      if (toastMessage) {
        const method = toastType === "error" ? toast.error : toast.success;
        method(toastMessage);
      }
    },
    updateSyncProgressFromRun(run) {
      if (!this.syncProgress.active || !run) {
        return;
      }

      const runId = Number(run?.id || 0);
      const runStatus = String(run?.status || "").toUpperCase();
      this.syncProgress.trackedRunId = runId;

      if (["RUNNING", "QUEUED"].includes(runStatus)) {
        this.syncProgress = {
          ...this.syncProgress,
          active: true,
          state: "running",
          title: "QuickBooks sync in progress",
          message:
            "The backend worker picked up the sync. This panel is polling until the run completes.",
          meta: [
            this.formatRunScope(run.entity_scope),
            run.started_at || run.created_at
              ? this.formatDate(run.started_at || run.created_at)
              : null,
          ]
            .filter(Boolean)
            .join(" • "),
        };
        return;
      }

      if (runStatus === "SUCCESS") {
        this.finishSyncTracking({
          state: "success",
          title: "QuickBooks sync completed",
          message:
            "The latest manual sync finished successfully. The recent runs and records below are up to date.",
          meta: this.summarizeRun(run),
          toastMessage: "QuickBooks sync completed.",
        });
        return;
      }

      if (["FAILED", "SKIPPED"].includes(runStatus)) {
        this.finishSyncTracking({
          state: "failed",
          title:
            runStatus === "SKIPPED"
              ? "QuickBooks sync was skipped"
              : "QuickBooks sync failed",
          message:
            run.last_error ||
            this.summarizeRun(run) ||
            "The latest manual sync did not finish successfully.",
          meta: [
            this.formatRunScope(run.entity_scope),
            run.completed_at ? this.formatDate(run.completed_at) : null,
          ]
            .filter(Boolean)
            .join(" • "),
          toastMessage:
            runStatus === "SKIPPED"
              ? "QuickBooks sync was skipped."
              : "QuickBooks sync failed.",
          toastType: "error",
        });
      }
    },
    async refreshSyncProgress() {
      await this.loadQuickbooksState({ silent: true });
    },
    async pollSyncProgress() {
      if (!this.syncProgress.active) {
        return;
      }

      if (this.syncProgress.timeoutAt && Date.now() >= this.syncProgress.timeoutAt) {
        this.finishSyncTracking({
          state: "timeout",
          title: "No sync completion yet",
          message:
            "The request was sent, but no finished manual run was observed within the expected window. The job may still be queued, or no worker may be consuming sync tasks.",
          meta: "Use Refresh status after starting the worker if you expect the sync to continue.",
          toastMessage: "QuickBooks sync is still waiting for worker activity.",
          toastType: "error",
        });
        return;
      }

      try {
        await this.loadQuickbooksState({ silent: true });

        const run = this.latestManualSyncRun;
        if (!run || ["RUNNING", "QUEUED"].includes(String(run?.status || "").toUpperCase())) {
          this.scheduleSyncPoll();
        }
      } catch (error) {
        console.error("Failed to poll QuickBooks sync progress", error);
        this.finishSyncTracking({
          state: "failed",
          title: "Unable to refresh sync status",
          message:
            error?.response?.data?.error?.message ||
            "The sync request was sent, but the UI could not refresh the status afterwards.",
          meta: "You can retry the refresh without re-triggering the sync.",
          toastMessage: "Unable to refresh QuickBooks sync status.",
          toastType: "error",
        });
      }
    },
    async loadQuickbooksState(options = {}) {
      const { silent = false } = options;

      if (!this.storeId) {
        this.settings = null;
        this.syncStatus = null;
        this.syncDetails = createDefaultSyncDetails();
        this.syncSettingsForm(null);
        return;
      }

      if (!silent) {
        this.loading = true;
      }
      this.errorMessage = "";

      try {
        const [settingsResponse, syncResponse, detailsResponse] = await Promise.all([
          http.get(api.qbo_settings(this.storeId)),
          http.get(api.qbo_sync_status(this.storeId)),
          http.get(api.qbo_sync_details(this.storeId)),
        ]);

        this.settings = settingsResponse?.data || null;
        this.syncStatus = syncResponse?.data || null;
        this.syncDetails = this.normalizeSyncDetails(detailsResponse?.data);
        this.syncSettingsForm(this.settings);

        // Auto-load payment mapping data if connected
        if (this.isConnected) {
          this.loadPaymentMappingData();
        }
      } catch (error) {
        console.error("Failed to load QuickBooks settings", error);
        this.errorMessage =
          error?.response?.data?.error?.message ||
          "Unable to load QuickBooks integration settings for this store.";
        this.syncDetails = createDefaultSyncDetails();
        if (silent) {
          throw error;
        }
      } finally {
        if (!silent) {
          this.loading = false;
        }
      }
    },
    async saveSettings() {
      if (!this.storeId || !this.hasSettingChanges) {
        return;
      }

      this.savingSettings = true;
      this.errorMessage = "";

      try {
        await http.patch(api.qbo_update_settings(this.storeId), {
          ...this.settingsForm,
        });
        await this.loadQuickbooksState();
        toast.success("QuickBooks settings updated successfully.");
      } catch (error) {
        console.error("Failed to save QuickBooks settings", error);
        this.errorMessage =
          error?.response?.data?.error?.message ||
          "Unable to save QuickBooks settings for this store.";
      } finally {
        this.savingSettings = false;
      }
    },
    async startConnectFlow() {
      if (!this.storeId || this.connecting) {
        return;
      }

      this.connecting = true;
      try {
        const response = await http.get(api.qbo_auth_url(this.storeId));
        const authorizationUrl = response?.data?.authorization_url;
        if (!authorizationUrl) {
          throw new Error("Missing QuickBooks authorization URL.");
        }
        window.location.assign(authorizationUrl);
      } catch (error) {
        console.error("Failed to start QuickBooks OAuth flow", error);
        const message =
          error?.response?.data?.error?.message ||
          error?.message ||
          "Unable to start the QuickBooks connection flow.";
        toast.error(message);
      } finally {
        this.connecting = false;
      }
    },
    async disconnect() {
      if (!this.storeId || this.disconnecting) {
        return;
      }

      this.disconnecting = true;
      try {
        await http.post(api.qbo_disconnect(this.storeId));
        toast.success("QuickBooks disconnected successfully.");
        await this.loadQuickbooksState();
      } catch (error) {
        console.error("Failed to disconnect QuickBooks", error);
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to disconnect QuickBooks for this store.",
        );
      } finally {
        this.disconnecting = false;
      }
    },
    async triggerSync() {
      if (!this.storeId || this.isSyncBusy || !this.canRunSync) {
        return;
      }

      this.syncing = true;
      try {
        this.beginSyncTracking();
        await http.post(api.qbo_trigger_sync(this.storeId));
        await this.loadQuickbooksState({ silent: true });
        this.scheduleSyncPoll();
      } catch (error) {
        console.error("Failed to start QuickBooks sync", error);
        this.stopSyncPolling();
        this.syncProgress = createDefaultSyncProgress();
        toast.error(
          error?.response?.data?.error?.message ||
            "Unable to start a QuickBooks sync for this store.",
        );
      } finally {
        this.syncing = false;
      }
    },
    formatDate(value) {
      try {
        return new Date(value).toLocaleString();
      } catch (error) {
        return value;
      }
    },
    toggleLabel(value) {
      return value ? "on" : "off";
    },
    // ---- Payment Account Mapping ----
    getMappingField(paymentAccountId, field) {
      const mapping = this.paymentAccountMappings.find(
        (m) => m.payment_account_id === paymentAccountId,
      );
      return mapping ? mapping[field] || "" : "";
    },
    async loadPaymentMappingData() {
      if (!this.storeId || !this.isConnected) return;
      this.loadingMappings = true;
      try {
        const [mappingsRes, accountsRes, depositRes, methodsRes] = await Promise.all([
          http.get(api.qbo_payment_account_mappings(this.storeId)),
          http.get(`/v1/store/${this.storeId}/payment-accounts`).catch(() => ({ data: [] })),
          http.get(api.qbo_deposit_accounts(this.storeId)).catch(() => ({ data: { accounts: [] } })),
          http.get(api.qbo_payment_methods(this.storeId)).catch(() => ({ data: { payment_methods: [] } })),
        ]);
        this.paymentAccountMappings = mappingsRes?.data || [];
        this.localPaymentAccounts = Array.isArray(accountsRes?.data)
          ? accountsRes.data
          : accountsRes?.data?.payment_accounts || [];
        this.qboDepositAccounts = depositRes?.data?.accounts || [];
        this.qboPaymentMethods = methodsRes?.data?.payment_methods || [];
      } catch (error) {
        console.error("Failed to load payment mapping data", error);
        toast.error("Unable to load payment account mappings.");
      } finally {
        this.loadingMappings = false;
      }
    },
    async updateMapping(paymentAccountId, type, selectedId) {
      this.savingMapping = paymentAccountId;
      try {
        const payload = { payment_account_id: paymentAccountId };
        if (type === "deposit") {
          const selected = this.qboDepositAccounts.find((a) => a.id === selectedId);
          payload.qbo_deposit_account_id = selectedId || null;
          payload.qbo_deposit_account_name = selected?.name || null;
        } else {
          const selected = this.qboPaymentMethods.find((m) => m.id === selectedId);
          payload.qbo_payment_method_id = selectedId || null;
          payload.qbo_payment_method_name = selected?.name || null;
        }
        const response = await http.post(
          api.qbo_payment_account_mappings(this.storeId),
          payload,
        );
        // Update the local cache
        const idx = this.paymentAccountMappings.findIndex(
          (m) => m.payment_account_id === paymentAccountId,
        );
        if (idx >= 0) {
          this.paymentAccountMappings.splice(idx, 1, response.data);
        } else {
          this.paymentAccountMappings.push(response.data);
        }
        toast.success("Payment account mapping saved.");
      } catch (error) {
        console.error("Failed to update mapping", error);
        toast.error("Unable to save payment account mapping.");
      } finally {
        this.savingMapping = null;
      }
    },
  },
};
</script>

<style scoped>
.quickbooks-panel {
  display: grid;
  gap: 1rem;
}

.quickbooks-card {
  display: grid;
  gap: 1rem;
  overflow: hidden;
}

.quickbooks-card__header,
.quickbooks-section__header,
.quickbooks-record-card__header {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.quickbooks-card__header {
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  padding-bottom: 0.9rem;
}

.quickbooks-card__eyebrow {
  align-items: center;
  color: var(--hs-primary, #3293ec);
  display: flex;
  font-size: 0.72rem;
  font-weight: 950;
  gap: 0.45rem;
  margin-bottom: 0.45rem;
  text-transform: uppercase;
}

.quickbooks-card__title,
.quickbooks-section h4 {
  color: var(--hs-text, #0f172a);
  font-weight: 950;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
}

.quickbooks-card__title {
  font-size: 1.2rem;
}

.quickbooks-section h4 {
  font-size: 0.98rem;
}

.quickbooks-card__subtitle,
.quickbooks-section p,
.quickbooks-footnote {
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.55;
  margin: 0.35rem 0 0;
  max-width: 54rem;
}

.quickbooks-actions,
.quickbooks-chip-row {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.quickbooks-loading,
.quickbooks-loading-list {
  display: grid;
  gap: 0.8rem;
}

.quickbooks-loading__grid {
  display: grid;
  gap: 0.8rem;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 12rem), 1fr));
}

.quickbooks-summary-grid {
  display: grid;
  gap: 0.7rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.quickbooks-summary-card,
.quickbooks-section,
.quickbooks-toggle-card,
.quickbooks-record-card,
.quickbooks-stat-card {
  background: color-mix(in srgb, var(--hs-surface, #ffffff) 96%, var(--hs-bg-soft, #f8fafc));
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  min-width: 0;
}

.quickbooks-summary-card {
  display: grid;
  gap: 0.45rem;
  min-height: 7rem;
  padding: 0.85rem;
}

.quickbooks-summary-card__top,
.quickbooks-stat-card__header {
  align-items: center;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
}

.quickbooks-summary-card__top span {
  color: var(--hs-muted, #64748b);
  font-size: 0.68rem;
  font-weight: 950;
  text-transform: uppercase;
}

.quickbooks-summary-card strong {
  color: var(--hs-text, #0f172a);
  display: block;
  font-size: 1rem;
  font-weight: 950;
  line-height: 1.25;
}

.quickbooks-summary-card p {
  color: var(--hs-muted, #64748b);
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.45;
  margin: 0;
  overflow-wrap: anywhere;
}

.quickbooks-sync-feedback {
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 18px;
  padding: 0.95rem;
}

.quickbooks-sync-feedback--waiting,
.quickbooks-sync-feedback--running {
  background: color-mix(in srgb, #06b6d4 8%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, #06b6d4 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.quickbooks-sync-feedback--success {
  background: color-mix(in srgb, #22c55e 8%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, #22c55e 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.quickbooks-sync-feedback--failed {
  background: color-mix(in srgb, #ef4444 8%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, #ef4444 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.quickbooks-sync-feedback--timeout {
  background: color-mix(in srgb, #f59e0b 9%, var(--hs-surface, #ffffff));
  border-color: color-mix(in srgb, #f59e0b 32%, var(--hs-border, rgba(148, 163, 184, 0.22)));
}

.quickbooks-sync-feedback__content {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.quickbooks-sync-feedback__heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.quickbooks-sync-feedback__heading p {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 950;
  margin: 0;
}

.quickbooks-sync-feedback__message,
.quickbooks-sync-feedback__meta {
  color: var(--hs-muted, #64748b);
  font-weight: 750;
  line-height: 1.55;
  margin: 0.35rem 0 0;
}

.quickbooks-sync-feedback__message {
  font-size: 0.84rem;
}

.quickbooks-sync-feedback__meta {
  font-size: 0.74rem;
}

.quickbooks-sync-feedback__progress {
  background: color-mix(in srgb, var(--hs-primary, #3293ec) 12%, transparent);
  border-radius: 999px;
  height: 0.4rem;
  margin-top: 0.85rem;
  overflow: hidden;
}

.quickbooks-sync-feedback__progress div {
  animation: quickbooks-progress 1.4s ease-in-out infinite;
  background: var(--hs-primary, #3293ec);
  border-radius: inherit;
  height: 100%;
  width: 36%;
}

.quickbooks-main-grid,
.quickbooks-secondary-grid {
  display: grid;
  gap: 1rem;
}

.quickbooks-main-grid {
  grid-template-columns: 1fr;
}

.quickbooks-secondary-grid {
  align-items: start;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.quickbooks-section {
  align-content: start;
  display: grid;
  gap: 0.9rem;
  padding: 1rem;
}

.quickbooks-section--wide {
  grid-column: auto;
}

.quickbooks-toggle-grid,
.quickbooks-form-grid,
.quickbooks-stats-grid {
  display: grid;
  gap: 0.65rem;
}

.quickbooks-toggle-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.quickbooks-form-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.quickbooks-stats-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.quickbooks-toggle-card {
  background: transparent;
  border-color: color-mix(in srgb, var(--hs-border, rgba(148, 163, 184, 0.22)) 78%, transparent);
  border-radius: 12px;
  padding: 0;
}

.quickbooks-toggle-card :deep(.hs-toggle) {
  align-items: center;
  display: flex;
  flex-direction: row-reverse;
  gap: 1rem;
  justify-content: space-between;
  min-height: 4.35rem;
  padding: 0.8rem 0.9rem;
  width: 100%;
}

.quickbooks-toggle-card :deep(.hs-toggle__content) {
  gap: 0.2rem;
  max-width: 44rem;
}

.quickbooks-toggle-card :deep(.hs-toggle strong) {
  font-size: 0.84rem;
}

.quickbooks-toggle-card :deep(.hs-toggle small) {
  font-size: 0.74rem;
  line-height: 1.45;
}

.quickbooks-toggle-card :deep(.hs-toggle__switch) {
  flex: 0 0 auto;
}

.quickbooks-list {
  display: grid;
  gap: 0.75rem;
}

.quickbooks-record-card,
.quickbooks-stat-card {
  padding: 0.9rem;
}

.quickbooks-secondary-grid .quickbooks-record-card {
  padding: 0.75rem 0.85rem;
}

.quickbooks-record-card__title,
.quickbooks-stat-card__title {
  align-items: center;
  color: var(--hs-text, #0f172a);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.9rem;
  font-weight: 950;
  gap: 0.45rem;
  line-height: 1.3;
  margin: 0;
}

.quickbooks-record-card__title--spaced {
  margin-top: 0.55rem;
}

.quickbooks-record-card__body {
  color: var(--hs-text, #0f172a);
  font-size: 0.84rem;
  font-weight: 750;
  line-height: 1.55;
  margin: 0.55rem 0 0;
  overflow-wrap: anywhere;
}

.quickbooks-record-card__meta,
.quickbooks-stat-card__meta,
.quickbooks-record-card__date {
  color: var(--hs-muted, #64748b);
  font-size: 0.74rem;
  font-weight: 750;
  line-height: 1.45;
  margin: 0.35rem 0 0;
}

.quickbooks-record-card__meta--strong {
  color: var(--hs-text, #0f172a);
}

.quickbooks-record-card__date {
  flex-shrink: 0;
  margin: 0;
}

.quickbooks-field-label {
  color: var(--hs-muted, #64748b);
  display: block;
  font-size: 0.72rem;
  font-weight: 950;
  margin-bottom: 0.35rem;
}

.quickbooks-stat-pills {
  display: grid;
  gap: 0.45rem;
  grid-template-columns: repeat(2, max-content);
}

.quickbooks-stat-pills span {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 11%, transparent);
  border-radius: 999px;
  color: var(--hs-text, #0f172a);
  font-size: 0.72rem;
  font-weight: 950;
  padding: 0.42rem 0.65rem;
  white-space: nowrap;
}

.quickbooks-stat-pills .quickbooks-stat-pill--success {
  background: color-mix(in srgb, #22c55e 14%, transparent);
  color: #15803d;
}

.quickbooks-stat-pills .quickbooks-stat-pill--warning {
  background: color-mix(in srgb, #f59e0b 16%, transparent);
  color: #b45309;
}

.quickbooks-stat-pills .quickbooks-stat-pill--danger {
  background: color-mix(in srgb, #ef4444 13%, transparent);
  color: #dc2626;
}

.quickbooks-progress {
  background: color-mix(in srgb, var(--hs-muted, #64748b) 13%, transparent);
  border-radius: 999px;
  height: 0.5rem;
  margin-top: 0.9rem;
  overflow: hidden;
}

.quickbooks-progress > div {
  display: flex;
  height: 100%;
  width: 100%;
}

.quickbooks-stat-breakdown {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin-top: 0.9rem;
}

.quickbooks-stat-breakdown > div {
  background: var(--hs-surface, #ffffff);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 12px;
  padding: 0.65rem;
}

.quickbooks-stat-breakdown p {
  color: var(--hs-muted, #64748b);
  font-size: 0.7rem;
  font-weight: 800;
  margin: 0.2rem 0 0;
}

.quickbooks-stat-breakdown p:first-child {
  font-size: 0.9rem;
  font-weight: 950;
  margin: 0;
}

.quickbooks-text-success {
  color: #15803d !important;
}

.quickbooks-text-warning {
  color: #b45309 !important;
}

.quickbooks-text-danger {
  color: #dc2626 !important;
}

.quickbooks-inline-error,
.quickbooks-empty-note {
  border-radius: 14px;
  font-weight: 750;
  line-height: 1.55;
}

.quickbooks-inline-error {
  background: color-mix(in srgb, #ef4444 8%, var(--hs-surface, #ffffff));
  border: 1px solid color-mix(in srgb, #ef4444 28%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  color: #b91c1c;
  display: grid;
  font-size: 0.76rem;
  gap: 0.25rem;
  margin-top: 0.9rem;
  padding: 0.75rem;
}

.quickbooks-inline-error p {
  color: inherit;
  font-size: 0.78rem;
  font-weight: 950;
  margin: 0;
}

.quickbooks-inline-error span,
.quickbooks-inline-error small {
  overflow-wrap: anywhere;
}

.quickbooks-empty-note {
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 70%, var(--hs-surface, #ffffff));
  border: 1px dashed var(--hs-border, rgba(148, 163, 184, 0.3));
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  margin: 0;
  padding: 1rem;
}

.quickbooks-secondary-grid .quickbooks-empty-note {
  align-self: start;
}

.quickbooks-footnote {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  padding-top: 0.9rem;
}

:deep(.ui-badge) {
  max-width: 100%;
}

@keyframes quickbooks-progress {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(280%);
  }
}

@media (max-width: 1024px) {
  .quickbooks-summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .quickbooks-main-grid,
  .quickbooks-secondary-grid,
  .quickbooks-stats-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .quickbooks-card__header,
  .quickbooks-section__header,
  .quickbooks-sync-feedback__content,
  .quickbooks-record-card__header {
    flex-direction: column;
  }

  .quickbooks-actions {
    width: 100%;
  }

  .quickbooks-actions :deep(.hs-button) {
    justify-content: center;
    width: 100%;
  }

  .quickbooks-toggle-grid,
  .quickbooks-summary-grid,
  .quickbooks-form-grid {
    grid-template-columns: 1fr;
  }

  .quickbooks-toggle-card :deep(.hs-toggle) {
    align-items: flex-start;
    min-height: 0;
  }

  .quickbooks-stat-pills {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }

  .quickbooks-stat-pills span {
    text-align: center;
  }
}
</style>
